import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { sanitizeUrl } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton/TextButton';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import WidgetBoxWrapper from './_partials/WidgetBoxWrapper/WidgetBoxWrapper';
import ZoneContext from '../../context/ZoneContext/ZoneContext';
import useGruser from '../../hooks/useGruser';
import useModule from '../../hooks/useModule';
import usePage from '../../hooks/usePage';
import WidgetHeaderMobile from './lego/WidgetHeaderMobile/WidgetHeaderMobile';
import WidgetHeader from './lego/WidgetHeader/WidgetHeader';
import {
  BiData,
  SeeAllClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import WidgetEditControls from './_partials/WidgetEditControls/WidgetEditControls';
import { getModuleDataFromPapiModule } from '../../redux/helpers';

import s from './WidgetBox.scss';

export interface Props {
  moduleId: number;
  header: React.ReactNode;
  subHeader?: string;
  className?: string;
  headerClassName?: string;
  dataHook?: string;
  children: React.ReactNode;
  headerExtras?: React.ReactNode;
  count?: number | string;
  seeAllUrl?: string;
  seeAllText?: string;
  configPopoverComponent?: React.ComponentType<any>;
  autoShowWidgetConfig?: boolean;
  confirmRemove?: (onConfirm: Function) => void;
}

const WidgetBox: React.FC<Props> = ({
  moduleId,
  header,
  subHeader,
  className,
  headerClassName,
  dataHook,
  children,
  headerExtras,
  count,
  seeAllUrl,
  configPopoverComponent,
  autoShowWidgetConfig,
  confirmRemove,
}) => {
  const { gruser } = useGruser();
  const gruserPage = usePage();
  const isMobile = useContext(MobileContext);
  const isDesktop = !isMobile;
  const direction = useContext(ZoneContext)?.direction || 'column';
  const module = useModule(moduleId);
  const { t } = useTranslation();
  if (!gruser || !module) {
    return null;
  }
  const moduleData = getModuleDataFromPapiModule(module);

  const renderHeaderContents = () => {
    if (!seeAllUrl) {
      return header;
    }

    return (
      <BiLink
        biData={BiData<SeeAllClickBiEvent>({
          evid: 106,
          creatorid: BiContextProvidedValue,
          typeid: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
        })}
        href={sanitizeUrl(seeAllUrl)}
      >
        {header}
      </BiLink>
    );
  };

  const renderRightSideSeeAllLink = () => {
    if (!moduleData['hasMore'] || !seeAllUrl) {
      return null;
    }
    return (
      <TextButton
        size="medium"
        variant="main"
        biData={BiData<SeeAllClickBiEvent>({
          evid: 106,
          typeid: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
          creatorid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
        })}
        href={sanitizeUrl(seeAllUrl)}
        className={s['see-all']}
        data-role="see-all-link"
      >
        {t('gruser.widgetbox.button.seeAll')}
      </TextButton>
    );
  };

  const renderHeaderExtraElements = () => {
    return (
      <div className={s['header-extras']}>
        {headerExtras}
        {isDesktop && (
          <WidgetEditControls
            moduleId={moduleId}
            direction={direction}
            configPopoverComponent={configPopoverComponent}
            autoShowWidgetConfig={autoShowWidgetConfig}
            confirmRemove={confirmRemove}
          />
        )}
        {renderRightSideSeeAllLink()}
      </div>
    );
  };

  return (
    <BiLoggerContextProvider
      value={{
        widgetname: module.name,
        widgetzone: module.zonename,
        sectionname:
          gruserPage?.name === 'deviation' ? 'deviation_widget' : undefined,
      }}
    >
      <WidgetBoxWrapper
        data-role="widget"
        className={c(s['root'], className)}
        data-hook={dataHook}
        data-moduleid={moduleId}
        data-userid={gruser.gruserId}
        id={`module-${moduleId}`}
      >
        {isMobile ? (
          <WidgetHeaderMobile
            rootClassName={headerClassName}
            header={renderHeaderContents()}
            subHeader={subHeader}
            count={count}
            isSticky={false}
            extras={renderHeaderExtraElements()}
          />
        ) : (
          <WidgetHeader
            className={headerClassName}
            header={renderHeaderContents()}
            subHeader={subHeader}
            count={count}
            extras={renderHeaderExtraElements()}
          />
        )}
        <div className={s['widget-content']}>{children}</div>
      </WidgetBoxWrapper>
    </BiLoggerContextProvider>
  );
};
WidgetBox.displayName = 'WidgetBox';

export default WidgetBox;
